import { useEffect, useState } from 'react';

const debounce = <T extends (...args: any[]) => void>(
  func: T,
  delay: number,
): ((...args: Parameters<T>) => void) => {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  };
};

// Define breakpoints
export const SIZE_XS = 'xs';
export const SIZE_SM = 'sm';
export const SIZE_MD = 'md';
export const SIZE_LG = 'lg';
export const SIZE_XL = 'xl';
export const SIZE_XXL = 'xxl';

type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

const resolveBreakpoint = (width: number): Breakpoint => {
  if (width < 576) {
    return SIZE_XS;
  } else if (width < 768) {
    return SIZE_SM;
  } else if (width < 992) {
    return SIZE_MD;
  } else if (width < 1200) {
    return SIZE_LG;
  } else if (width < 1440) {
    return SIZE_XL;
  } else {
    return SIZE_XXL;
  }
};

const useBreakpoint = (): Breakpoint => {
  const [size, setSize] = useState<Breakpoint>(() =>
    resolveBreakpoint(typeof window !== 'undefined' ? window.innerWidth : 0),
  );

  useEffect(() => {
    const handleResize = debounce(() => {
      setSize(
        resolveBreakpoint(
          typeof window !== 'undefined' ? window.innerWidth : 0,
        ),
      );
    }, 200);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return size;
};

export default useBreakpoint;
